import Controller from '@ember/controller';
import { action } from '@ember/object';
import type RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import type Store from '@ember-data/store';
import { tracked } from '@glimmer/tracking';
import type AjaxFetchService from 'garaje/services/ajax-fetch';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type StateService from 'garaje/services/state';
import { AtlasEditorTab } from 'garaje/utils/atlas';
import { TrackedArray, TrackedObject } from 'tracked-built-ins';

import type MapsSignageEditRoute from './route';

type ConfigState = Record<string, unknown>;

export default class MapsSignageEditController extends Controller {
  @service declare ajax: AjaxFetchService;
  @service declare store: Store;
  @service declare state: StateService;
  @service declare flashMessages: FlashMessagesService;
  @service declare router: RouterService;

  @tracked declare model: MapsSignageEditRoute;
  @tracked tab: AtlasEditorTab = AtlasEditorTab.GENERAL;
  @tracked config = new TrackedObject({});

  // Below is all for the undo/redo logic

  @tracked undoStack = new TrackedArray<ConfigState>([]);
  @tracked redoStack = new TrackedArray<ConfigState>([]);

  @action
  initHistory(): void {
    this.undoStack = new TrackedArray([]);
    this.redoStack = new TrackedArray([]);
    this.recordChange();
  }

  get stringConfig(): string {
    return JSON.stringify(this.config);
  }

  @action
  recordChange(): void {
    const last = this.undoStack[this.undoStack.length - 1] || {};
    const current = this.cloneConfig(this.config as unknown as ConfigState);

    if (JSON.stringify(last) !== JSON.stringify(current)) {
      this.undoStack.push(current);
      this.redoStack = new TrackedArray([]);
    }
  }

  cloneConfig(state: ConfigState): ConfigState {
    return JSON.parse(JSON.stringify(state)) as ConfigState;
  }

  get canUndo(): boolean {
    return this.undoStack.length > 1;
  }

  get canRedo(): boolean {
    return this.redoStack.length > 0;
  }

  @action
  undo(): void {
    if (!this.canUndo) {
      return;
    }

    this.redoStack.push(this.undoStack.pop()!);

    const previous = this.undoStack[this.undoStack.length - 1]!;

    this.config = new TrackedObject(this.cloneConfig(previous));
  }

  @action
  redo(): void {
    if (!this.canRedo) {
      return;
    }

    const next = this.redoStack.pop()!;

    this.undoStack.push(next);
    this.config = new TrackedObject(this.cloneConfig(next));
  }
}
